import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { select, Store } from '@ngrx/store';

import { User } from '../../../users/models/user';
import { UserPasswordRequest } from '../../../users/models/user-password.request';
import { UserService } from '../../../users/services/user.service';
import { AuthService } from '../../services/auth.service';
import * as fromAuth from './../../../store';
import { CompletePasswordPresenter } from './utils/presenter/complete-password.presenter';
import { NotificationService } from '../../../shared/services/notification.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-update-password',
  templateUrl: './update-password.component.html',
  styleUrls: ['./update-password.component.scss'],
  providers: [CompletePasswordPresenter]
})
export class UpdatePasswordComponent implements OnInit, OnDestroy {
  user: User;
  isUpdating = false;
  private helper = new JwtHelperService();
  notifications: any;

  constructor(
    public completePasswordPresenter: CompletePasswordPresenter,
    private storeAuth: Store<fromAuth.AuthState>,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {
    this.translate
      .get('changePasswordModal.notifications')
      .subscribe(res => (this.notifications = res));
  }

  ngOnInit() {
    this.route.data.subscribe(data => {
      const token = data.auth.data.token;
      this.storeAuth
        .pipe(select(fromAuth.selectSessionUser))
        .subscribe(user => (this.user = user));
      this.storeAuth.dispatch(fromAuth.loginSuccess({ access_token: token }));
      this.storeAuth.dispatch(
        fromAuth.findUser({ id: this.helper.decodeToken(token).uuid })
      );
    });
  }
  onSubmit(formValue) {
    const formFormated = this.trimForm(formValue);
    this.completePasswordPresenter.form.patchValue({ ...formFormated });
    if (this.formValid) {
      this.isUpdating = true;
      const userPassword = this.addOperationType();
      this.userService.setPassword(this.user.id, userPassword).subscribe({
        next: data => {
          this.isUpdating = false;
          this.authService.cleanSession();
          this.router.navigate(['/login']);
        },
        error: error => {
          this.isUpdating = false;
          const message = error.error.error;
          switch (message) {
            case 'SAME_PASSWORD':
              this.notificationService.error(
                this.notifications.passwordShouldNotBeTheSame
              );
              break;
            case 'USER_DISABLED':
              this.notificationService.error(this.notifications.userDisabled);
              break;

            default:
              this.notificationService.error(this.notifications.generic);
              break;
          }
        }
      });
    }
  }

  get formValid() {
    return this.completePasswordPresenter.form.valid;
  }

  private addOperationType(): UserPasswordRequest {
    return {
      ...this.completePasswordPresenter.form.value,
      type: 'COMPLETE_PASSWORD'
    };
  }

  private trimForm(formValue: { password: string; confirmPassword: string }) {
    let { password, confirmPassword } = formValue;
    password = password.trim();
    confirmPassword = confirmPassword.trim();
    return {
      password,
      confirmPassword
    };
  }

  get controlByName() {
    return (name: string) => this.completePasswordPresenter[name];
  }

  get errorPasswordMinLengthA() {
    return this.completePasswordPresenter.form
      .get('password')
      .hasError('minlength');
  }

  get errorPasswordMaxLengthA() {
    return this.completePasswordPresenter.form
      .get('password')
      .hasError('maxlength');
  }

  get errorPasswordMinLengthB() {
    return this.completePasswordPresenter.form
      .get('confirmPassword')
      .hasError('minlength');
  }

  get errorPasswordMaxLengthB() {
    return this.completePasswordPresenter.form
      .get('confirmPassword')
      .hasError('maxlength');
  }

  get errorNotSame() {
    return this.completePasswordPresenter.form.hasError('notSame');
  }

  get errorPasswordPattern() {
    if (
      this.completePasswordPresenter.form.get('password').hasError('pattern')
    ) {
      return this.completePasswordPresenter.form
        .get('password')
        .hasError('pattern');
    }
    if (
      this.completePasswordPresenter.form
        .get('confirmPassword')
        .hasError('pattern')
    ) {
      return this.completePasswordPresenter.form
        .get('confirmPassword')
        .hasError('pattern');
    }
  }

  isInvalid(nameControl: string): boolean {
    const control = this.controlByName(nameControl);
    const status = control.invalid && (control.dirty || control.touched);
    return status;
  }

  ngOnDestroy() {
    this.authService.cleanSession();
  }
}
